import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Icon from '@mdi/react';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';

/**
 * Vertical room, in pixels, reserved inside the viewport so that content hanging below the row
 * (field error messages, for instance) is not clipped by the horizontal scroll container.
 */
const DEFAULT_BOTTOM_OVERFLOW_ALLOWANCE = 48;

/** Sits above `InputError`'s z-index of 10, so a hanging error never covers the chevron. */
const SCROLL_INDICATOR_Z_INDEX = 11;

interface HorizontalScrollAreaProps {
	children: React.ReactNode;
	className?: string;
	bottomOverflowAllowance?: number;
}

/**
 * Scrolls its content horizontally without showing a scrollbar. When the content overflows, a
 * chevron shows over the edge it overflows towards. The chevron is a non-interactive indicator.
 *
 * A horizontal scroll container also clips vertically, so the viewport reserves
 * `bottomOverflowAllowance` pixels of padding below its content and pulls the same amount back with
 * a negative margin: content that hangs below the row stays visible while the box keeps its height.
 */
export function HorizontalScrollArea({
	children,
	className,
	bottomOverflowAllowance = DEFAULT_BOTTOM_OVERFLOW_ALLOWANCE,
}: HorizontalScrollAreaProps) {
	const viewportRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
	const [canScrollRight, setCanScrollRight] = useState<boolean>(false);

	const refreshOverflow = useCallback(() => {
		const viewport = viewportRef.current;
		if (!viewport) return;
		const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;
		setCanScrollLeft(viewport.scrollLeft > 1);
		setCanScrollRight(viewport.scrollLeft < maxScrollLeft - 1);
	}, []);

	useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport) return undefined;

		refreshOverflow();

		const resizeObserver = new ResizeObserver(refreshOverflow);
		resizeObserver.observe(viewport);
		Array.from(viewport.children).forEach((child) => resizeObserver.observe(child));
		viewport.addEventListener('scroll', refreshOverflow);

		return () => {
			resizeObserver.disconnect();
			viewport.removeEventListener('scroll', refreshOverflow);
		};
	}, [refreshOverflow, children]);

	return (
		<Container className={className}>
			<Viewport ref={viewportRef} $bottomOverflowAllowance={bottomOverflowAllowance}>
				{children}
			</Viewport>
			{canScrollLeft && (
				<ScrollIndicator $side="left" data-testid="scroll-left-indicator">
					<Icon path={mdiChevronLeft} size={1} />
				</ScrollIndicator>
			)}
			{canScrollRight && (
				<ScrollIndicator $side="right" data-testid="scroll-right-indicator">
					<Icon path={mdiChevronRight} size={1} />
				</ScrollIndicator>
			)}
		</Container>
	);
}

const Container = styled.div`
	/* flow-root keeps the viewport's negative bottom margin from collapsing out, so the box keeps the
	   height of its row while content hanging below it paints outside. */
	display: flow-root;
	position: relative;
	width: 100%;
`;

const Viewport = styled.div<{ $bottomOverflowAllowance: number }>(
	({ $bottomOverflowAllowance }) => css`
		align-items: center;
		display: flex;
		justify-content: flex-start;
		margin-bottom: -${$bottomOverflowAllowance}px;
		overflow-x: auto;
		padding-bottom: ${$bottomOverflowAllowance}px;
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}

		& > * {
			flex-shrink: 0;
		}
	`,
);

const ScrollIndicator = styled.div<{ $side: 'left' | 'right' }>(
	({ theme, $side }) => css`
		align-items: center;
		background-color: ${theme.palette.light.main};
		border-radius: ${$side === 'right'
			? `0 ${theme.spacing(2)} ${theme.spacing(2)} 0`
			: `${theme.spacing(2)} 0 0 ${theme.spacing(2)}`};
		bottom: 0;
		color: ${theme.palette.text.primary};
		display: flex;
		justify-content: center;
		left: ${$side === 'left' ? 0 : 'auto'};
		opacity: 0.8;
		pointer-events: none;
		position: absolute;
		right: ${$side === 'right' ? 0 : 'auto'};
		top: 0;
		width: ${theme.spacing(5)};
		/* Above the field error messages hanging out of the row (InputError paints at z-index 10). */
		z-index: ${SCROLL_INDICATOR_Z_INDEX};
	`,
);
